import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const KOSOVO_CENTER = [42.6026, 20.9030];
const REFRESH_INTERVAL = 30000;

function createIcon(color) {
  return L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;background:${color};border:2px solid white;border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.4)"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -10],
  });
}

function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.flyTo(center, zoom || 12, { duration: 1.5 });
    }
  }, [map, center, zoom]);
  return null;
}

function getPointColor(point) {
  if (point.active_alarms > 0) {
    const hasCritical = point.sensors?.some(s => s.status === 'alarm');
    if (hasCritical) return '#ef4444';
    return '#f59e0b';
  }
  if (point.status === 'online') return '#22c55e';
  return '#6b7280';
}

function StatusDot({ status }) {
  const colors = {
    online: 'bg-green-500',
    offline: 'bg-gray-400',
    alarm: 'bg-red-500',
    warning: 'bg-amber-500',
  };
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${colors[status] || 'bg-gray-400'}`} />
  );
}

export default function Map() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [points, setPoints] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filterRegion, setFilterRegion] = useState('');
  const [mapCenter, setMapCenter] = useState(KOSOVO_CENTER);
  const [mapZoom, setMapZoom] = useState(9);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

  const fetchPoints = useCallback(async (regionId) => {
    try {
      const url = regionId
        ? `${BACKEND_URL}/api/map/points?region_id=${regionId}`
        : `${BACKEND_URL}/api/map/points`;
      const res = await axios.get(url, { headers: authHeader });
      setPoints(res.data || []);
    } catch (err) {
      console.error('Error fetching map points:', err);
    }
  }, []);

  const fetchRegions = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/regions`, { headers: authHeader });
      setRegions(res.data || []);
    } catch (err) {
      console.error('Error fetching regions:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchRegions(), fetchPoints('')]);
      setLoading(false);
    };
    init();

    intervalRef.current = setInterval(() => {
      fetchPoints(filterRegion);
    }, REFRESH_INTERVAL);

    return () => clearInterval(intervalRef.current);
  }, []);

  useEffect(() => {
    fetchPoints(filterRegion);
  }, [filterRegion]);

  const handleRegionChange = (e) => {
    const regionId = e.target.value;
    setFilterRegion(regionId);
    if (regionId) {
      const region = regions.find(r => String(r.id) === String(regionId));
      if (region?.latitude && region?.longitude) {
        setMapCenter([parseFloat(region.latitude), parseFloat(region.longitude)]);
        setMapZoom(region.zoom || 11);
      }
    } else {
      setMapCenter(KOSOVO_CENTER);
      setMapZoom(9);
    }
  };

  const handlePointClick = (point) => {
    if (point.latitude && point.longitude) {
      setMapCenter([parseFloat(point.latitude), parseFloat(point.longitude)]);
      setMapZoom(14);
    }
  };

  const totalPoints = points.length;
  const onlinePoints = points.filter(p => p.status === 'online').length;
  const alarmPoints = points.filter(p => p.active_alarms > 0).length;

  return (
    <Layout>
      <div className="flex flex-col h-full gap-4 p-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            Harta e Sensorëve
          </h1>
          <select
            value={filterRegion}
            onChange={handleRegionChange}
            className="w-full sm:w-56 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Të gjitha rajonet</option>
            {regions.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 text-center">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalPoints}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Pika totale</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 text-center">
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{onlinePoints}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Online</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 text-center">
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{alarmPoints}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Me alarme</p>
          </div>
        </div>

        {/* Main content: map + sidebar */}
        <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
          {/* Map */}
          <div className="flex-1 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm" style={{ minHeight: '400px' }}>
            {!loading && (
              <MapContainer
                center={KOSOVO_CENTER}
                zoom={9}
                style={{ height: '100%', width: '100%', minHeight: '400px' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapController center={mapCenter} zoom={mapZoom} />
                {points.map(point => {
                  const lat = parseFloat(point.latitude);
                  const lng = parseFloat(point.longitude);
                  if (!lat || !lng) return null;
                  const color = getPointColor(point);
                  return (
                    <Marker
                      key={point.id}
                      position={[lat, lng]}
                      icon={createIcon(color)}
                    >
                      <Popup>
                        <div className="min-w-[180px]">
                          <p className="font-semibold text-gray-900 text-sm mb-1">{point.name}</p>
                          {point.company_name && (
                            <p className="text-xs text-gray-500 mb-2">{point.company_name}</p>
                          )}
                          {point.active_alarms > 0 && (
                            <p className="text-xs font-medium text-red-600 mb-2">
                              {point.active_alarms} alarm{point.active_alarms > 1 ? 'e' : ''} aktiv{point.active_alarms > 1 ? 'e' : ''}
                            </p>
                          )}
                          {point.sensors && point.sensors.length > 0 && (
                            <div className="space-y-1">
                              {point.sensors.map(s => (
                                <div key={s.id} className="flex items-center gap-2 text-xs">
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.status === 'online' ? 'bg-green-500' : s.status === 'alarm' ? 'bg-red-500' : 'bg-gray-400'}`} />
                                  <span className="text-gray-700 truncate">{s.name}</span>
                                  {s.temperature != null && (
                                    <span className="text-orange-600 font-medium ml-auto">{s.temperature}°C</span>
                                  )}
                                  {s.humidity != null && (
                                    <span className="text-blue-600 font-medium">{s.humidity}%</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            )}
            {loading && (
              <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-sm">Duke ngarkuar hartën...</p>
              </div>
            )}
          </div>

          {/* Sidebar list */}
          <div className="lg:w-72 xl:w-80 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Pikat e monitorimit</p>
            </div>
            <div className="overflow-y-auto flex-1">
              {points.length === 0 && !loading && (
                <p className="text-sm text-gray-500 dark:text-gray-400 p-4 text-center">Asnjë pikë</p>
              )}
              {points.map(point => {
                const color = getPointColor(point);
                const avgTemp = point.sensors?.length > 0
                  ? (point.sensors.reduce((a, s) => a + (s.temperature || 0), 0) / point.sensors.length).toFixed(1)
                  : null;
                return (
                  <button
                    key={point.id}
                    onClick={() => handlePointClick(point)}
                    className="w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
                        style={{ backgroundColor: color }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{point.name}</p>
                        {point.company_name && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{point.company_name}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          {avgTemp != null && (
                            <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">{avgTemp}°C</span>
                          )}
                          {point.active_alarms > 0 && (
                            <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 text-xs rounded font-medium">
                              {point.active_alarms} alarm
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 shadow-sm">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 self-center">Legjenda:</p>
          {[
            { color: '#22c55e', label: 'Online' },
            { color: '#ef4444', label: 'Alarm kritik' },
            { color: '#f59e0b', label: 'Paralajmërim' },
            { color: '#6b7280', label: 'Offline' },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
              <span className="text-xs text-gray-600 dark:text-gray-400">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
